'use client';

import { useEffect, useState } from 'react';

const SPINNER_STYLE = 'brand';

const SKIP_ROUTES = ['/dashboard'];

const shouldSkip = (path) =>
  SKIP_ROUTES.some(route => path.startsWith(route));

const getPageName = (pathname) => {
  if (!pathname || pathname === '/') return 'Home';
  const segments = pathname.replace(/^\//, '').split('/');
  const last = segments[segments.length - 1];
  return last
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

function RingSpinner() {
  return (
    <div className="relative w-14 h-14">
      {/* Outer ring */}
      <div className="absolute inset-0 rounded-full border-[3px] border-red-800/10 border-t-red-800 animate-spin" />
      {/* Middle ring - reverse spin via style since Tailwind has no reverse */}
      <div
        className="absolute inset-2 rounded-full border-[3px] border-red-800/20 border-b-red-800 animate-spin"
        style={{ animationDirection: 'reverse', animationDuration: '0.6s' }}
      />
      {/* Center dot */}
      <div className="absolute inset-5 rounded-full bg-red-800 animate-pulse" />
    </div>
  );
}

function DotsSpinner() {
  return (
    <div className="flex gap-3 items-center">
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="w-3 h-3 rounded-full bg-red-800 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function PulseSpinner() {
  return (
    <div className="relative w-16 h-16">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border-2 border-red-800 animate-ping"
          style={{ animationDelay: `${i * 0.5}s`, animationDuration: '1.8s' }}
        />
      ))}
      <div className="absolute inset-5 rounded-full bg-red-800" />
    </div>
  );
}

function PlaneSpinner() {
  return (
    <div className="text-center">
      <div
        className="text-3xl inline-block animate-bounce"
        style={{ animationDuration: '1.2s' }}
      >
        ✈️
      </div>
      <div className="mt-3 flex gap-1 justify-center">
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="w-1.5 h-0.5 rounded-sm bg-red-800 animate-pulse"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
      <div
        className="mt-2 text-xs font-semibold tracking-widest text-red-800 animate-pulse"
      >
        LOADING...
      </div>
    </div>
  );
}

function BrandSpinner({ pageName }) {
  return (
    <div className="text-center py-2">

      {/* Animated plane */}
      <div
        className="text-base inline-block mb-3 animate-bounce"
        style={{ animationDuration: '1.4s' }}
      >
        ✈️
      </div>

      {/* DU GLOBAL brand text */}
      <div className="flex items-center justify-center gap-1.5 mb-2">
        {/* DU */}
        <div className="flex gap-px">
          {'DU'.split('').map((char, i) => (
            <span
              key={i}
              className="text-[15px] font-normal text-red-800 tracking-widest inline-block animate-bounce"
              style={{
                animationDelay: `${i * 0.1}s`,
                animationDuration: '1.4s',
                fontFamily: 'sans-serif',
              }}
            >
              {char}
            </span>
          ))}
        </div>

        {/* Divider dot */}
        <div className="w-1 h-1 rounded-full bg-red-800 opacity-50 mb-0.5" />

        {/* GLOBAL */}
        <div className="flex gap-px">
          {'GLOBAL'.split('').map((char, i) => (
            <span
              key={i}
              className="text-[15px] font-normal text-gray-900 tracking-widest inline-block animate-bounce"
              style={{
                animationDelay: `${(i + 3) * 0.1}s`,
                animationDuration: '1.4s',
                fontFamily: 'sans-serif',
              }}
            >
              {char}
            </span>
          ))}
        </div>
      </div>

      {/* Animated underline */}
      <div className="h-[3px] rounded-sm bg-gradient-to-r from-transparent via-red-800 to-transparent animate-pulse mb-3" />

      {/* Page name */}
      {pageName && (
        <div className="text-[11px] text-gray-400 tracking-wide mb-2.5 animate-fade-in">
          Loading{' '}
          <span className="text-red-800 font-semibold">{pageName}</span>
          ...
        </div>
      )}

      {/* Dot trail */}
      <div className="flex gap-1.5 justify-center">
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full animate-bounce ${
              i % 2 === 0 ? 'bg-red-800' : 'bg-gray-900'
            }`}
            style={{
              animationDelay: `${i * 0.15}s`,
              animationDuration: '1.2s',
            }}
          />
        ))}
      </div>

    </div>
  );
}

function ActiveSpinner({ pageName }) {
  if (SPINNER_STYLE === 'ring') return <RingSpinner />;
  if (SPINNER_STYLE === 'dots') return <DotsSpinner />;
  if (SPINNER_STYLE === 'pulse') return <PulseSpinner />;
  if (SPINNER_STYLE === 'plane') return <PlaneSpinner />;
  if (SPINNER_STYLE === 'brand') return <BrandSpinner pageName={pageName} />;
  return <RingSpinner />;
}

export default function InstantLoaderInner({ pathname, searchParams }) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [pageName, setPageName] = useState('');

  const isSkipped = shouldSkip(pathname);

  useEffect(() => {
    const handleShow = (e) => {
      setTimeout(() => {
        if (e?.detail?.pathname) {
          setPageName(getPageName(e.detail.pathname));
        }
        setVisible(true);
        setAnimating(true);
      }, 0);
    };

    const handleHide = () => {
      setTimeout(() => {
        setAnimating(false);
        setTimeout(() => {
          setVisible(false);
          setPageName('');
        }, 300);
      }, 0);
    };

    window.addEventListener('instant-loader:show', handleShow);
    window.addEventListener('instant-loader:hide', handleHide);

    return () => {
      window.removeEventListener('instant-loader:show', handleShow);
      window.removeEventListener('instant-loader:hide', handleHide);
    };
  }, []);

  useEffect(() => {
    setAnimating(false);
    setTimeout(() => {
      setVisible(false);
      setPageName('');
    }, 300);
  }, [pathname, searchParams]);

  if (isSkipped || !visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center
        bg-white/75 backdrop-blur-sm transition-opacity duration-300
        ${animating ? 'opacity-100' : 'opacity-0'}`}
    >
      <div
        className={`bg-white rounded-2xl px-10 py-8 shadow-2xl flex flex-col
          items-center border border-red-800/10 transition-transform duration-300
          ${animating ? 'scale-100' : 'scale-90'}`}
      >
        <ActiveSpinner pageName={pageName} />
      </div>
    </div>
  );
}
